import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { BUY_MAX_OPEN, BUY_MAX_PHOTOS, type BuyHandover, type BuyPayout } from "../../shared/buy";
import { adultBornBy, argentinaDate, isSealedCategory, MARKET_CONDITIONS } from "../../shared/market";
import { RATED_ZONES, ZONE_LABEL, type RatedZone } from "../../shared/shipping";
import { CONDITION_LABEL, LANGUAGE_LABEL, VARIANT_LANGUAGES, type Condition } from "../../shared/variants";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import { ApiError, imageUrl } from "../lib/api";
import { isPhotoType } from "../lib/resizeImage";
import { CardPicker } from "../market/CardPicker";
import type { CatalogCard } from "../market/api";
import { buyApi } from "./api";

interface Item {
  key: number;
  productId: number | null;
  name: string;
  detail: string;
  image: string | null;
  sealed: boolean;
  condition: Condition;
  language: string;
  quantity: number;
  note: string;
}

const STEPS = [
  { title: "buy.step1", text: "buy.step1Text" },
  { title: "buy.step2", text: "buy.step2Text" },
  { title: "buy.step3", text: "buy.step3Text" },
] as const;
// Any size: it's brought down before it goes up (resizeImage).
const photoOk = isPhotoType;

/** /compramos — people offer their cards to the store; the store answers with a price later. */
export function BuyPage() {
  const { t } = useI18n();
  const auth = useAuth();
  useEffect(() => {
    document.title = `${t("buy.metaTitle")} | pokefans.fun`;
  }, [t]);

  return (
    <div className="sell-page buy-page">
      <section className="sell-hero">
        <div className="container sell-hero-inner">
          <h1>{t("buy.title")}</h1>
          <p className="sell-lead">{t("buy.intro")}</p>
        </div>
      </section>

      <section className="container section" aria-label={t("buy.title")}>
        <ol className="sell-steps">
          {STEPS.map((s, i) => (
            <li key={s.title}>
              <span className="sell-step-n" aria-hidden="true">
                {i + 1}
              </span>
              <div>
                <h3>{t(s.title)}</h3>
                <p>{t(s.text)}</p>
              </div>
            </li>
          ))}
        </ol>
        <p className="notice notice-warn">{t("buy.terms")}</p>
        <p className="muted">
          {t("buy.otherMarket")} <Link to="/vender">{t("sell.nav")} →</Link>
        </p>
      </section>

      <section className="container section">
        {auth.user ? (
          <BuyForm />
        ) : (
          !auth.loading && (
            <Link to={`/ingresar?next=${encodeURIComponent("/compramos")}`} className="btn btn-accent btn-lg">
              {t("buy.login")}
            </Link>
          )
        )}
      </section>
    </div>
  );
}

function BuyForm() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState<Item[]>([]);
  const [freeText, setFreeText] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [form, setForm] = useState({ handover: "meet" as BuyHandover, zone: "caba" as RatedZone, location: "", availability: "", contactPhone: "", payout: "transfer" as BuyPayout, birthDate: "", notes: "" });
  const [pokemonOnly, setPokemonOnly] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const bad = (k: string) => errors.includes(k);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  const add = (item: Omit<Item, "key" | "condition" | "language" | "quantity" | "note">) =>
    setItems((list) => [...list, { ...item, key: Date.now() + list.length, condition: item.sealed ? "SEALED" : "NM", language: "en", quantity: 1, note: "" }]);
  const pick = (c: CatalogCard) =>
    add({ productId: c.id, name: c.name, detail: [c.setName, c.cardNumber ? `#${c.cardNumber}` : null].filter(Boolean).join(" · "), image: c.imageKey, sealed: isSealedCategory(c.category) });
  const change = (key: number, patch: Partial<Item>) => setItems((list) => list.map((i) => (i.key === key ? { ...i, ...patch } : i)));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const local = [
      ...(items.length ? [] : ["items"]),
      ...(photos.length ? [] : ["photos"]),
      ...(form.birthDate && form.birthDate > adultBornBy(argentinaDate(new Date())) ? ["birthDate_minor"] : []),
    ];
    if (local.length) return setErrors(local);
    setBusy(true);
    setErrors([]);
    try {
      const created = await buyApi.create({
        ...form,
        zone: form.handover === "meet" ? form.zone : null,
        pokemonOnly,
        items: items.map((i) => ({ productId: i.productId, description: i.productId ? "" : i.name, condition: i.condition, language: i.language, quantity: i.quantity, note: i.note })),
      });
      try {
        for (const p of photos) await buyApi.uploadPhoto(created.number, p);
        await buyApi.act(created.number, "send");
      } catch {
        // The draft stays in "Mis cartas ofrecidas", where the photos can be added again.
        navigate(`/cuenta/compramos?error=photos`);
        return;
      }
      navigate("/cuenta/compramos?sent=1");
    } catch (err) {
      setErrors(err instanceof ApiError ? ((err.details as { fields?: string[] })?.fields ?? [err.code]) : ["generic"]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="stack buy-form" onSubmit={submit} noValidate>
      <fieldset className="stack">
        <legend>{t("buy.cards")}</legend>
        <CardPicker label={t("buy.findCard")} onPick={pick} />
        <div className="field">
          <label htmlFor="buy-free">{t("buy.notFound")}</label>
          <div className="field-row">
            <input id="buy-free" value={freeText} maxLength={200} placeholder={t("buy.notFoundPlaceholder")} onChange={(e) => setFreeText(e.target.value)} />
            <button
              type="button"
              className="btn btn-outline"
              disabled={freeText.trim().length < 3}
              onClick={() => {
                add({ productId: null, name: freeText.trim(), detail: "", image: null, sealed: false });
                setFreeText("");
              }}
            >
              {t("buy.add")}
            </button>
          </div>
        </div>
        {items.length === 0 ? (
          <p className={bad("items") ? "form-error" : "muted"}>{bad("items") ? t("buy.errorItems") : t("buy.noCards")}</p>
        ) : (
          <ul className="buy-items">
            {items.map((i) => (
              <li key={i.key} className="buy-item">
                {i.image && <img src={imageUrl(i.image)} alt="" width={48} height={67} loading="lazy" />}
                <div className="stack-sm">
                  <strong>{i.name}</strong>
                  {i.detail && <span className="muted small">{i.detail}</span>}
                  <div className="buy-item-fields">
                    <label>
                      {t("buy.condition")}{" "}
                      <select value={i.condition} disabled={i.sealed} onChange={(e) => change(i.key, { condition: e.target.value as Condition })}>
                        {(i.sealed ? (["SEALED"] as const) : MARKET_CONDITIONS).map((c) => (
                          <option key={c} value={c}>
                            {CONDITION_LABEL[c][locale]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {t("buy.language")}{" "}
                      <select value={i.language} onChange={(e) => change(i.key, { language: e.target.value })}>
                        {VARIANT_LANGUAGES.map((l) => (
                          <option key={l} value={l}>
                            {LANGUAGE_LABEL[l]?.[locale] ?? l}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {t("buy.quantity")} <input type="number" min={1} max={100} value={i.quantity} onChange={(e) => change(i.key, { quantity: Math.max(1, Math.min(100, Number(e.target.value) || 1)) })} />
                    </label>
                  </div>
                  <input aria-label={`${t("buy.itemNote")}: ${i.name}`} placeholder={t("buy.itemNote")} value={i.note} maxLength={300} onChange={(e) => change(i.key, { note: e.target.value })} />
                </div>
                <button type="button" className="btn btn-link btn-sm" onClick={() => setItems((list) => list.filter((x) => x.key !== i.key))}>
                  {t("buy.remove")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </fieldset>

      <div className="field">
        <label htmlFor="buy-photos">{t("buy.photos")}</label>
        <input
          id="buy-photos"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          aria-invalid={bad("photos")}
          onChange={(e) => setPhotos([...(e.target.files ?? [])].filter(photoOk).slice(0, BUY_MAX_PHOTOS))}
        />
        <span className="field-hint">{photos.length ? t("buy.photosChosen", { count: photos.length }) : t("buy.photosHint", { max: BUY_MAX_PHOTOS })}</span>
        {bad("photos") && <span className="field-error">{t("buy.errorPhotos")}</span>}
      </div>

      <fieldset className="radio-cards">
        <legend>{t("buy.handover")}</legend>
        {(["meet", "store", "ship"] as const).map((h) => (
          <label key={h} className={form.handover === h ? "radio-card is-selected" : "radio-card"}>
            <input type="radio" name="handover" value={h} checked={form.handover === h} onChange={() => setForm({ ...form, handover: h })} />
            <span>
              <strong>{t(`buy.handover.${h}`)}</strong>
              <span className="muted small">{t(`buy.handover.${h}Hint`)}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {form.handover === "meet" && (
        <div className="field">
          <label htmlFor="buy-zone">{t("buy.zone")}</label>
          <select id="buy-zone" value={form.zone} onChange={set("zone")}>
            {RATED_ZONES.map((z) => (
              <option key={z} value={z}>
                {ZONE_LABEL[z][locale]}
              </option>
            ))}
          </select>
        </div>
      )}
      {form.handover !== "store" && (
        <div className="field">
          <label htmlFor="buy-location">{form.handover === "ship" ? t("buy.locationShip") : t("buy.location")}</label>
          <input id="buy-location" value={form.location} maxLength={200} onChange={set("location")} aria-invalid={bad("location")} />
        </div>
      )}
      <div className="field">
        <label htmlFor="buy-when">{t("buy.availability")}</label>
        <input id="buy-when" value={form.availability} maxLength={300} placeholder={t("buy.availabilityPlaceholder")} onChange={set("availability")} />
      </div>
      <div className="field">
        <label htmlFor="buy-phone">{t("buy.phone")}</label>
        <input id="buy-phone" type="tel" autoComplete="tel" value={form.contactPhone} maxLength={30} onChange={set("contactPhone")} aria-invalid={bad("contactPhone")} />
      </div>
      <fieldset className="radio-cards">
        <legend>{t("buy.payout")}</legend>
        {(["transfer", "cash"] as const).map((p) => (
          <label key={p} className={form.payout === p ? "radio-card is-selected" : "radio-card"}>
            <input type="radio" name="payout" value={p} checked={form.payout === p} onChange={() => setForm({ ...form, payout: p })} />
            <span>
              <strong>{t(`buy.payout.${p}`)}</strong>
            </span>
          </label>
        ))}
      </fieldset>
      <div className="field">
        <label htmlFor="buy-birth">{t("buy.birthDate")}</label>
        <input id="buy-birth" type="date" value={form.birthDate} onChange={set("birthDate")} aria-invalid={bad("birthDate") || bad("birthDate_minor")} />
        <span className="field-hint">{t("buy.birthDateHint")}</span>
        {bad("birthDate_minor") && <span className="field-error">{t("buy.birthDateMinor")}</span>}
      </div>
      <div className="field">
        <label htmlFor="buy-notes">{t("buy.notes")}</label>
        <textarea id="buy-notes" rows={3} maxLength={1000} value={form.notes} onChange={set("notes")} />
      </div>
      <label className="check">
        <input type="checkbox" checked={pokemonOnly} onChange={(e) => setPokemonOnly(e.target.checked)} /> <span>{t("buy.pokemonOnly")}</span>
      </label>
      {errors.includes("blocked") ? (
        <p className="form-error" role="alert">
          {t("buy.blocked")}
        </p>
      ) : errors.includes("too_many_open") ? (
        <p className="form-error" role="alert">
          {t("buy.errorTooMany", { max: BUY_MAX_OPEN })}
        </p>
      ) : (
        errors.some((e) => !["items", "photos", "birthDate_minor"].includes(e)) && (
          <p className="form-error" role="alert">
            {t("buy.errorFields")}
          </p>
        )
      )}
      <button type="submit" className="btn btn-primary btn-lg" disabled={busy || !pokemonOnly}>
        {busy ? t("buy.sending") : t("buy.send")}
      </button>
    </form>
  );
}
